import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { reportsAPI, usersAPI, trainingNodesAPI, sopsAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  FileText, 
  Download,
  User,
  Calendar,
  CheckCircle,
  FileQuestion,
  BookOpen,
  GraduationCap,
  Loader2,
  Building2
} from 'lucide-react';
import { toast } from 'sonner';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

const Reports = () => {
  const { canManage, user } = useAuth();
  const [users, setUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [report, setReport] = useState(null);
  const [allTrainings, setAllTrainings] = useState([]);
  const [allSOPs, setAllSOPs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [activeTab, setActiveTab] = useState('training');
  const trainingReportRef = useRef(null);
  const sopReportRef = useRef(null);

  useEffect(() => {
    loadStaticData();
    if (canManage) {
      loadUsers();
    } else {
      setSelectedUserId(user.id);
      loadReport(user.id);
    }
  }, [canManage, user]);

  const loadStaticData = async () => {
    try {
      const [trainingsRes, sopsRes] = await Promise.all([
        trainingNodesAPI.getAll(),
        sopsAPI.getAll()
      ]);
      setAllTrainings(trainingsRes.data);
      setAllSOPs(sopsRes.data);
    } catch (error) {
      console.error('Failed to load static data:', error);
    }
  };

  const loadUsers = async () => {
    try {
      const response = await usersAPI.getAll();
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to load users:', error);
    }
  };

  const loadReport = async (userId) => {
    if (!userId) return;
    
    setLoading(true);
    try {
      const response = await reportsAPI.getByUser(userId);
      setReport(response.data);
    } catch (error) {
      console.error('Failed to load report:', error);
      toast.error('Failed to load report');
    } finally {
      setLoading(false);
    }
  };

  const handleUserChange = (userId) => {
    setSelectedUserId(userId);
    loadReport(userId);
  };

  const handleDownloadPDF = async (reportType) => {
    const ref = reportType === 'training' ? trainingReportRef : sopReportRef;
    if (!ref.current || !report) return;
    
    setGenerating(true);
    try {
      const canvas = await html2canvas(ref.current, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff'
      });
      
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const imgWidth = 210;
      const pageHeight = 295;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      let position = 0;
      
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
      
      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }
      
      const fileName = reportType === 'training' 
        ? `individual-training-record-${report.user.name.replace(/\s+/g, '-')}.pdf`
        : `sop-training-record-${report.user.name.replace(/\s+/g, '-')}.pdf`;
      
      pdf.save(fileName);
      toast.success(`${reportType === 'training' ? 'Training' : 'SOP'} report downloaded successfully`);
    } catch (error) {
      console.error('Failed to generate PDF:', error);
      toast.error('Failed to generate PDF');
    } finally {
      setGenerating(false);
    }
  };

  // Get training completion data
  const getTrainingData = () => {
    if (!report) return [];
    
    // Mandatory trainings from the Individual Training Record Form
    const mandatoryTrainings = [
      { title: "Company Handbook Acceptance", target_staff: "All Stryde staff involved in any study conduct", training_method: "Online" },
      { title: "Good Clinical Practice (GCP)", target_staff: "All Stryde staff involved in any study conduct", training_method: "Online" },
      { title: "Dangerous Goods Transport (IATA)", target_staff: "CRCs", training_method: "Online" },
      { title: "HIPAA Training", target_staff: "All Stryde staff involved in any study conduct", training_method: "Online" },
      { title: "eClinpro Training", target_staff: "Regulatory and/or Study Coordinators", training_method: "Online" },
      { title: "CRIO Training (As applicable)", target_staff: "Regulatory and/or Study Coordinators", training_method: "Online" },
      { title: "Role specific onboarding training videos", target_staff: "All Stryde admin and study staff", training_method: "Online" },
    ];

    const completedIds = new Set(report.completed_training_nodes?.map(c => c.node_id) || []);
    
    return mandatoryTrainings.map(training => {
      // Find matching completed training
      const matchingTraining = allTrainings.find(t => 
        t.label.toLowerCase().includes(training.title.toLowerCase().split(' ')[0]) ||
        training.title.toLowerCase().includes(t.label.toLowerCase().split(' ')[0])
      );
      
      const isCompleted = matchingTraining ? completedIds.has(matchingTraining.id) : false;
      const completionData = report.completed_training_nodes?.find(c => 
        matchingTraining && c.node_id === matchingTraining.id
      );
      
      return {
        ...training,
        completed: isCompleted,
        completion_date: completionData?.completed_at || null,
        comments: ""
      };
    });
  };

  // Get SOP completion data
  const getSOPData = () => {
    if (!report) return [];
    
    const completedIds = new Set(report.sop_completions?.map(c => c.sop_id) || []);
    
    return allSOPs.map(sop => {
      const isCompleted = completedIds.has(sop.id);
      const completionData = report.sop_completions?.find(c => c.sop_id === sop.id);
      
      return {
        sop_name: sop.title || `${sop.sop_number}_${sop.title}`,
        target_staff: sop.target_staff || "All Stryde admin and study staff",
        training_method: sop.training_method || "Online",
        completed: isCompleted,
        completion_date: completionData?.completed_at || null,
        comments: ""
      };
    });
  };

  if (!canManage && !user) {
    return null;
  }

  const trainingData = getTrainingData();
  const sopData = getSOPData();

  return (
    <div className="space-y-6 animate-fade-in" data-testid="reports-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-heading font-bold text-slate-900 flex items-center gap-3">
            <FileText className="w-8 h-8 text-blue-600" />
            Training Records
          </h1>
          <p className="text-slate-600 mt-1">
            Generate Individual Training Record and SOP Training Record forms
          </p>
        </div>
      </div>

      {/* User Selector (for managers/admins) */}
      {canManage && (
        <Card className="border-slate-200 shadow-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-4">
              <User className="w-5 h-5 text-slate-400" />
              <div className="flex-1 max-w-md">
                <Select value={selectedUserId} onValueChange={handleUserChange}>
                  <SelectTrigger data-testid="user-select">
                    <SelectValue placeholder="Select an employee to view report" />
                  </SelectTrigger>
                  <SelectContent>
                    {users.map(u => (
                      <SelectItem key={u.id} value={u.id}>
                        {u.name} ({u.email}) - {u.job_role || 'No role'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <div className="w-8 h-8 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
        </div>
      )}

      {/* No Selection */}
      {!loading && !report && canManage && (
        <Card className="border-slate-200 shadow-card">
          <CardContent className="text-center py-12">
            <FileText className="w-16 h-16 mx-auto text-slate-300 mb-4" />
            <p className="text-slate-500">Select an employee to view their training records</p>
          </CardContent>
        </Card>
      )}

      {/* Report Content */}
      {!loading && report && (
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <div className="flex items-center justify-between">
            <TabsList className="bg-slate-100">
              <TabsTrigger value="training" className="data-[state=active]:bg-white">
                <GraduationCap className="w-4 h-4 mr-2" />
                Individual Training Record
              </TabsTrigger>
              <TabsTrigger value="sop" className="data-[state=active]:bg-white">
                <BookOpen className="w-4 h-4 mr-2" />
                SOP Training Record
              </TabsTrigger>
            </TabsList>
            <Button 
              onClick={() => handleDownloadPDF(activeTab)}
              disabled={generating}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="download-pdf-btn"
            >
              {generating ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Download className="w-4 h-4 mr-2" />
              )}
              Download PDF
            </Button>
          </div>

          {/* Individual Training Record Form */}
          <TabsContent value="training">
            <div ref={trainingReportRef} className="bg-white p-6">
              <Card className="border-2 border-slate-300">
                <CardHeader className="border-b-2 border-slate-300 bg-slate-50">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-16 h-16 bg-blue-600 rounded-lg flex items-center justify-center">
                        <Building2 className="w-10 h-10 text-white" />
                      </div>
                      <div>
                        <CardTitle className="text-xl font-bold text-slate-900">
                          Individual Training Record Form
                        </CardTitle>
                      </div>
                    </div>
                    <div className="text-right text-sm text-slate-600">
                      <p>Document No: TR-001</p>
                      <p>Version: 1.0</p>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="p-0">
                  {/* Employee Info Section */}
                  <div className="grid grid-cols-2 border-b-2 border-slate-300">
                    <div className="p-4 border-r border-slate-200">
                      <p className="text-sm text-slate-500">Employee Name</p>
                      <p className="font-semibold text-slate-900">{report.user.name}</p>
                    </div>
                    <div className="p-4">
                      <p className="text-sm text-slate-500">Job Role</p>
                      <p className="font-semibold text-slate-900">{report.user.job_role || 'Not specified'}</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 border-b-2 border-slate-300">
                    <div className="p-4 border-r border-slate-200">
                      <p className="text-sm text-slate-500">Email</p>
                      <p className="font-semibold text-slate-900">{report.user.email}</p>
                    </div>
                    <div className="p-4">
                      <p className="text-sm text-slate-500">Report Generated</p>
                      <p className="font-semibold text-slate-900">
                        {new Date(report.generated_at).toLocaleDateString('en-US', {
                          year: 'numeric', month: 'long', day: 'numeric'
                        })}
                      </p>
                    </div>
                  </div>

                  {/* Training Table */}
                  <table className="w-full">
                    <thead>
                      <tr className="bg-slate-100 border-b-2 border-slate-300">
                        <th className="text-left px-4 py-3 text-sm font-bold text-slate-700 border-r border-slate-200">Training Title</th>
                        <th className="text-left px-4 py-3 text-sm font-bold text-slate-700 border-r border-slate-200">Target Staff</th>
                        <th className="text-center px-4 py-3 text-sm font-bold text-slate-700 border-r border-slate-200 w-24">Training Method</th>
                        <th className="text-center px-4 py-3 text-sm font-bold text-slate-700 border-r border-slate-200 w-32">Date of Completion</th>
                        <th className="text-left px-4 py-3 text-sm font-bold text-slate-700 w-40">Comments</th>
                      </tr>
                    </thead>
                    <tbody>
                      {trainingData.map((training, index) => (
                        <tr key={index} className={`border-b border-slate-200 ${training.completed ? 'bg-emerald-50' : ''}`}>
                          <td className="px-4 py-3 text-sm text-slate-900 border-r border-slate-200">
                            {training.title}
                          </td>
                          <td className="px-4 py-3 text-sm text-slate-600 border-r border-slate-200">
                            {training.target_staff}
                          </td>
                          <td className="px-4 py-3 text-sm text-center text-slate-600 border-r border-slate-200">
                            {training.training_method}
                          </td>
                          <td className="px-4 py-3 text-sm text-center border-r border-slate-200">
                            {training.completion_date ? (
                              <span className="text-emerald-700 font-medium">
                                {new Date(training.completion_date).toLocaleDateString()}
                              </span>
                            ) : (
                              <span className="text-slate-400">-</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-sm text-slate-600">
                            {training.completed ? (
                              <Badge className="bg-emerald-100 text-emerald-700">
                                <CheckCircle className="w-3 h-3 mr-1" />
                                Complete
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="text-amber-600 border-amber-300">
                                Pending
                              </Badge>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  {/* Signature Section */}
                  <div className="grid grid-cols-2 border-t-2 border-slate-300 mt-4">
                    <div className="p-4 border-r border-slate-200">
                      <p className="text-sm text-slate-500 mb-8">Employee Signature</p>
                      <div className="border-t border-slate-300 pt-2">
                        <p className="text-sm text-slate-600">{report.user.name}</p>
                      </div>
                    </div>
                    <div className="p-4">
                      <p className="text-sm text-slate-500 mb-8">Supervisor Signature</p>
                      <div className="border-t border-slate-300 pt-2">
                        <p className="text-sm text-slate-600">Date: ______________</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* SOP Training Record Form */}
          <TabsContent value="sop">
            <div ref={sopReportRef} className="bg-white p-6">
              <Card className="border-2 border-slate-300">
                <CardHeader className="border-b-2 border-slate-300 bg-slate-50">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-16 h-16 bg-blue-600 rounded-lg flex items-center justify-center">
                        <Building2 className="w-10 h-10 text-white" />
                      </div>
                      <div>
                        <CardTitle className="text-xl font-bold text-slate-900">
                          SOP Training Record Form
                        </CardTitle>
                      </div>
                    </div>
                    <div className="text-right text-sm text-slate-600">
                      <p>Document No: SOP-TR-001</p>
                      <p>Version: 1.0</p>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="p-0">
                  {/* Employee Info Section */}
                  <div className="grid grid-cols-2 border-b-2 border-slate-300">
                    <div className="p-4 border-r border-slate-200">
                      <p className="text-sm text-slate-500">Employee Name</p>
                      <p className="font-semibold text-slate-900">{report.user.name}</p>
                    </div>
                    <div className="p-4">
                      <p className="text-sm text-slate-500">Job Role</p>
                      <p className="font-semibold text-slate-900">{report.user.job_role || 'Not specified'}</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 border-b-2 border-slate-300">
                    <div className="p-4 border-r border-slate-200">
                      <p className="text-sm text-slate-500">Email</p>
                      <p className="font-semibold text-slate-900">{report.user.email}</p>
                    </div>
                    <div className="p-4">
                      <p className="text-sm text-slate-500">Report Generated</p>
                      <p className="font-semibold text-slate-900">
                        {new Date(report.generated_at).toLocaleDateString('en-US', {
                          year: 'numeric', month: 'long', day: 'numeric'
                        })}
                      </p>
                    </div>
                  </div>

                  {/* SOP Table */}
                  <table className="w-full">
                    <thead>
                      <tr className="bg-slate-100 border-b-2 border-slate-300">
                        <th className="text-left px-4 py-3 text-sm font-bold text-slate-700 border-r border-slate-200">SOP Name</th>
                        <th className="text-left px-4 py-3 text-sm font-bold text-slate-700 border-r border-slate-200">Target Staff</th>
                        <th className="text-center px-4 py-3 text-sm font-bold text-slate-700 border-r border-slate-200 w-24">Training Method</th>
                        <th className="text-center px-4 py-3 text-sm font-bold text-slate-700 border-r border-slate-200 w-32">Date of Completion</th>
                        <th className="text-left px-4 py-3 text-sm font-bold text-slate-700 w-40">Comments</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sopData.map((sop, index) => (
                        <tr key={index} className={`border-b border-slate-200 ${sop.completed ? 'bg-emerald-50' : ''}`}>
                          <td className="px-4 py-3 text-sm text-slate-900 border-r border-slate-200">
                            {sop.sop_name}
                          </td>
                          <td className="px-4 py-3 text-sm text-slate-600 border-r border-slate-200">
                            {sop.target_staff}
                          </td>
                          <td className="px-4 py-3 text-sm text-center text-slate-600 border-r border-slate-200">
                            {sop.training_method}
                          </td>
                          <td className="px-4 py-3 text-sm text-center border-r border-slate-200">
                            {sop.completion_date ? (
                              <span className="text-emerald-700 font-medium">
                                {new Date(sop.completion_date).toLocaleDateString()}
                              </span>
                            ) : (
                              <span className="text-slate-400">-</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-sm text-slate-600">
                            {sop.completed ? (
                              <Badge className="bg-emerald-100 text-emerald-700">
                                <CheckCircle className="w-3 h-3 mr-1" />
                                Complete
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="text-amber-600 border-amber-300">
                                Pending
                              </Badge>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  {/* Signature Section */}
                  <div className="grid grid-cols-2 border-t-2 border-slate-300 mt-4">
                    <div className="p-4 border-r border-slate-200">
                      <p className="text-sm text-slate-500 mb-8">Employee Signature</p>
                      <div className="border-t border-slate-300 pt-2">
                        <p className="text-sm text-slate-600">{report.user.name}</p>
                      </div>
                    </div>
                    <div className="p-4">
                      <p className="text-sm text-slate-500 mb-8">Supervisor Signature</p>
                      <div className="border-t border-slate-300 pt-2">
                        <p className="text-sm text-slate-600">Date: ______________</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
};

export default Reports;
